import axios from 'axios'
import toast from 'react-hot-toast'

// Create axios instance
const rawBaseURL = import.meta.env.VITE_API_URL
const normalizedBaseURL = rawBaseURL
  ? `${rawBaseURL}`.replace(/\/+$/, '').endsWith('/api')
    ? `${rawBaseURL}`.replace(/\/+$/, '')
    : `${rawBaseURL}`.replace(/\/+$/, '') + '/api'
  : '/api'

export const api = axios.create({
  baseURL: normalizedBaseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    const selectedClientId = localStorage.getItem('selectedClientId')
    const url = config.url || ''
    if (selectedClientId && (
      url.startsWith('/finance') ||
      url.startsWith('/taxes') ||
      url.startsWith('/analytics') ||
      url.startsWith('/dashboard') ||
      url.startsWith('/budgets') ||
      url.startsWith('/budget-control')
    )) {
      config.headers['x-client-id'] = selectedClientId
    }
    try {
      // Auto-attach fiscal_year to relevant GET requests if not provided
      if ((config.method || 'get').toLowerCase() === 'get') {
        const fy = localStorage.getItem('selectedFiscalYear') || String(new Date().getFullYear())
        const needsFY = [
          url.startsWith('/budgets'),
          url.startsWith('/budget-control'),
          url.startsWith('/analytics'),
          url.startsWith('/dashboard')
        ].some(Boolean)
        if (needsFY) {
          if (!config.params || typeof config.params.fiscal_year === 'undefined') {
            config.params = { ...(config.params || {}), fiscal_year: fy }
          }
        }
      }
    } catch (_) { void 0 }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const message = error.response?.data?.message || error.message || 'An error occurred'
    
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      const code = error.response?.data?.code
      if (code === 'SESSION_TIMEOUT') {
        window.dispatchEvent(new CustomEvent('session-timeout', { detail: { code, message } }))
      } else {
        // Dispatch event for AuthContext to handle logout
        window.dispatchEvent(new Event('unauthorized'))
      }
    }

    // Don't show toast for certain status codes
    // 403 Forbidden might be our "Active Session" error, we want to show that one
    if (error.response?.status !== 404 && error.response?.status !== 401) {
       // Let the component handle 403 if it has specific logic, otherwise show toast
       // For active session error, we definitely want to show it.
       if (error.response?.data?.code === 'ACTIVE_SESSION_EXISTS') {
         // This will be handled by the login component
         // toast.error(message, { duration: 6000 });
       } else {
         toast.error(message);
       }
    }
    
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
  logout: () => api.post('/auth/logout'),
  ping: () => api.post('/auth/ping'),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data)
}

// Purchase Orders API
export const purchaseOrdersAPI = {
  getAll: (params = {}) => api.get('/purchase-orders', { params }),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  addUsage: (id, data) => api.post(`/purchase-orders/${id}/add-usage`, data)
}

// Vendor POs API
export const vendorPOsAPI = {
  getAll: (params = {}) => api.get('/vendor-pos', { params }),
  getById: (id) => api.get(`/vendor-pos/${id}`),
  create: (data) => api.post('/vendor-pos', data),
  update: (id, data) => api.put(`/vendor-pos/${id}`, data),
  delete: (id) => api.delete(`/vendor-pos/${id}`),
  receiveGoods: (id, data) => api.post(`/vendor-pos/${id}/receive`, data),
  createShipment: (id, data) => api.post(`/vendor-pos/${id}/shipments`, data),
  getShipments: (id) => api.get(`/vendor-pos/${id}/shipments`),
  getAllShipments: (params = {}) => api.get('/vendor-shipments', { params })
}

// GRN API
export const grnAPI = {
  getAll: (params = {}) => api.get('/grn', { params }),
  getById: (id) => api.get(`/grn/${id}`),
  create: (data) => api.post('/grn', data)
}

// Usage Logs API
export const usageLogsAPI = {
  getAll: (params = {}) => api.get('/usage-logs', { params }),
  getById: (id) => api.get(`/usage-logs/${id}`),
  getByPO: (poId, params = {}) => api.get(`/usage-logs/po/${poId}`, { params }),
  delete: (id) => api.delete(`/usage-logs/${id}`)
}

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getOverview: () => api.get('/dashboard/overview'),
  getAlerts: () => api.get('/dashboard/alerts')
}

// Inventory API
export const inventoryAPI = {
  getAll: (params) => api.get('/inventory', { params }),
  getById: (id) => api.get(`/inventory/${id}`),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
  getCategories: () => api.get('/inventory/categories')
}

export const inventorySettingsAPI = {
  // Categories
  getCategories: (params = {}) => api.get('/inventory-settings/categories', { params }),
  createCategory: (data) => api.post('/inventory-settings/categories', data),
  updateCategory: (id, data) => api.put(`/inventory-settings/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/inventory-settings/categories/${id}`),

  // UOM
  getUOMs: (params = {}) => api.get('/inventory-settings/uom', { params }),
  createUOM: (data) => api.post('/inventory-settings/uom', data),
  updateUOM: (id, data) => api.put(`/inventory-settings/uom/${id}`, data),
  deleteUOM: (id) => api.delete(`/inventory-settings/uom/${id}`)
}

export const inventoryAIAPI = {
  suggestItems: (params = {}) => api.get('/inventory/ai/suggest', { params }),
  enrichItem: (data) => api.post('/inventory/ai/enrich', data),
  getVendorIntel: (params = {}) => api.get('/inventory/ai/vendor-intel', { params })
}

// Invoices API
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  requestDelete: (id, data) => api.post(`/invoices/${id}/delete-request`, data),
  addPayment: (id, data) => api.post(`/invoices/${id}/payments`, data),
  logGeneration: (id, data) => api.post(`/invoices/${id}/log-generation`, data),
  getPODetails: (type, id) => api.get(`/invoices/po-details/${type}/${id}`)
}

// Payments API
export const paymentsAPI = {
  getAll: (params = {}) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`)
}

// Clients API
export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`)
}

// Contracts API
export const contractsAPI = {
  getAll: (params) => api.get('/contracts', { params }),
  getById: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  getStats: (params) => api.get('/contracts/stats', { params })
}

// Upload API
export const uploadAPI = {
  upload: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
}

// Requisitions API
export const requisitionsAPI = {
  getStats: () => api.get('/requisitions/stats'),
  getAll: (params = {}) => api.get('/requisitions', { params }),
  getById: (id) => api.get(`/requisitions/${id}`),
  create: (data) => api.post('/requisitions', data),
  update: (id, data) => api.put(`/requisitions/${id}`, data),
  delete: (id, data) => api.delete(`/requisitions/${id}`, { data }),
  updateStatus: (id, status, comments) => api.post(`/requisitions/${id}/status`, { status, comments })
}

// RFx API
export const rfxAPI = {
  getAll: (params = {}) => api.get('/rfx', { params }),
  getStats: () => api.get('/rfx/stats'),
  getById: (id) => api.get(`/rfx/${id}`),
  create: (data) => api.post('/rfx/create', data),
  update: (id, data) => api.put(`/rfx/${id}`, data),
  delete: (id) => api.delete(`/rfx/${id}`),
  inviteVendors: (id, vendorIds) => api.post(`/rfx/${id}/invite`, { vendorIds }),
  updateStatus: (id, status) => api.put(`/rfx/${id}/status`, { status }),
  createBid: (id, data) => api.post(`/rfx/${id}/bids`, data),
  getBids: (id) => api.get(`/rfx/${id}/bids`),
  getMyBids: () => api.get('/rfx/my-bids'),
  evaluateBid: (bidId, data) => api.put(`/rfx/bids/${bidId}/evaluate`, data)
}

// License API
export const licenseAPI = {
  getStatus: () => api.get('/license/status'),
  activate: (data) => api.post('/license/activate', data),
  getSessions: (params) => api.get('/license/sessions', { params }),
  generateTest: (data) => api.post('/license/generate-test', data),
  list: (params = {}) => api.get('/license/licenses', { params }),
  revoke: (id) => api.post(`/license/revoke/${id}`)
}

export const billingAPI = {
  getPlans: () => api.get('/billing/plans'),
  createPlan: (data) => api.post('/billing/plans', data),
  getSubscription: () => api.get('/billing/subscription'),
  subscribe: (data) => api.post('/billing/subscription/subscribe', data),
  changeSubscription: (data) => api.post('/billing/subscription/change', data),
  generateInvoice: (data = {}) => api.post('/billing/invoices/generate', data),
  listTransactions: (params = {}) => api.get('/billing/transactions', { params }),
  markPaid: (id, data = {}) => api.post(`/billing/transactions/${id}/mark-paid`, data)
}


// Vendors API
export const vendorsAPI = {
  getAll: (params = {}) => api.get('/vendors', { params }),
  getById: (id) => api.get(`/vendors/${id}`),
  getLocations: (id, params = {}) => api.get(`/vendors/${id}/locations`, { params }),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`)
}

// Quotations API
export const quotationsAPI = {
  getAll: (params) => api.get('/quotations', { params }),
  getById: (id) => api.get(`/quotations/${id}`),
  create: (data) => api.post('/quotations', data),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  delete: (id) => api.delete(`/quotations/${id}`),
  compare: (rfxId) => api.get(`/quotations/compare/${rfxId}`),
  award: (id) => api.post(`/quotations/${id}/award`),
  updateStatus: (id, status) => api.patch(`/quotations/${id}/status`, { status })
}

// Budgets API
export const budgetsAPI = {
  getAll: (params = {}) => api.get('/budgets', { params }),
  getDashboardStats: (params = {}) => api.get('/budgets/dashboard-stats', { params }),
  getById: (id) => api.get(`/budgets/${id}`),
  create: (data) => api.post('/budgets', data),
  update: (id, data) => api.put(`/budgets/${id}`, data),
  delete: (id) => api.delete(`/budgets/${id}`),
  checkReserve: (data) => api.post('/budgets/check-reserve', data),
  transfer: (data) => api.post('/budgets/transfer', data),
  adjust: (data) => api.post('/budgets/adjust', data)
}

// Budget Control API
export const budgetControlAPI = {
  checkAvailability: (data) => api.post('/budget-control/check', data),
  getRules: (params = {}) => api.get('/budget-control/rules', { params }),
  getRuleById: (id) => api.get(`/budget-control/rules/${id}`),
  createRule: (data) => api.post('/budget-control/rules', data),
  updateRule: (id, data) => api.put(`/budget-control/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/budget-control/rules/${id}`),
  
  getOverrides: (params = {}) => api.get('/budget-control/overrides', { params }),
  requestOverride: (data) => api.post('/budget-control/override', data),
  updateOverride: (id, data) => api.put(`/budget-control/overrides/${id}`, data),
  deleteOverride: (id) => api.delete(`/budget-control/overrides/${id}`),
  
  getStats: (params = {}) => api.get('/budget-control/stats', { params }),
  getTransactions: (params = {}) => api.get('/budget-control/transactions', { params }),
  getAvailabilityLogs: (params = {}) => api.get('/budget-control/availability-logs', { params })
}

// Departments & Designations
export const departmentsAPI = {
  getAll: (params = {}) => api.get('/departments', { params }),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`)
}

export const designationsAPI = {
  getAll: (params = {}) => api.get('/designations', { params }),
  create: (data) => api.post('/designations', data),
  update: (id, data) => api.put(`/designations/${id}`, data),
  delete: (id) => api.delete(`/designations/${id}`)
}

export const businessUnitsAPI = {
  getAll: (params) => api.get('/business-units', { params }),
  create: (data) => api.post('/business-units', data),
  update: (id, data) => api.put(`/business-units/${id}`, data),
  delete: (id) => api.delete(`/business-units/${id}`)
}

// Analytics API
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  updateAll: (data) => api.put('/settings', data),
  getCurrency: () => api.get('/settings/currency'),
  updateCurrency: (data) => api.put('/settings/currency', data),
  refreshCurrency: (base) => api.get('/settings/currency/refresh', { params: { base } })
};

export const analyticsAPI = {
  getSpend: (params) => api.get('/analytics/spend', { params }),
  getSnapshot: (params) => api.get('/analytics/snapshot', { params }),
  getRiskMetrics: (params) => api.get('/analytics/risk-metrics', { params }),
  getVendorPerformance: (params) => api.get('/analytics/vendor-performance', { params }),
  getMaverickSpend: (params) => api.get('/analytics/maverick-spend', { params }),
  getPriceVariance: (params) => api.get('/analytics/price-variance', { params }),
  getInsights: (params) => api.get('/analytics/insights', { params })
};

export const intelligenceAPI = {
  getModuleUsage: (params = {}) => api.get('/intelligence/module-usage', { params }),
  getRecommendations: (params = {}) => api.get('/intelligence/recommendations', { params }),
  getAnomalies: (params = {}) => api.get('/intelligence/anomalies', { params }),
  chat: (data) => api.post('/intelligence/chat', data)
};

export const financeAPI = {
  getARAging: (params = {}) => api.get('/finance/ar/aging', { params }),
  getARRevenue: (params = {}) => api.get('/finance/ar/revenue', { params }),
  getBankAccounts: (params = {}) => api.get('/finance/cashbank/accounts', { params }),
  getBankAccountById: (id, params = {}) => api.get(`/finance/cashbank/accounts/${id}`, { params }),
  createBankAccount: (data, params = {}) => api.post('/finance/cashbank/accounts', data, { params }),
  updateBankAccount: (id, data, params = {}) => api.put(`/finance/cashbank/accounts/${id}`, data, { params }),
  deleteBankAccount: (id, params = {}) => api.delete(`/finance/cashbank/accounts/${id}`, { params }),
  getBankTransactions: (params = {}) => api.get('/finance/cashbank/transactions', { params }),
  createBankTransaction: (data, params = {}) => api.post('/finance/cashbank/transactions', data, { params }),
  updateBankTransaction: (id, data, params = {}) => api.put(`/finance/cashbank/transactions/${id}`, data, { params }),
  deleteBankTransaction: (id, params = {}) => api.delete(`/finance/cashbank/transactions/${id}`, { params }),
  getReconciliations: (params = {}) => api.get('/finance/cashbank/reconciliations', { params }),
  createReconciliation: (data, params = {}) => api.post('/finance/cashbank/reconciliations', data, { params }),
  completeReconciliation: (id, params = {}) => api.post(`/finance/cashbank/reconciliations/${id}/complete`, {}, { params }),
  getCashflow: (params = {}) => api.get('/finance/cashbank/cashflow', { params }),

  getGLAccounts: (params = {}) => api.get('/finance/gl/accounts', { params }),
  initDefaultGLAccounts: (params = {}) => api.post('/finance/gl/accounts/init', {}, { params }),
  createGLAccount: (data, params = {}) => api.post('/finance/gl/accounts', data, { params }),

  getGLJournalEntries: (params = {}) => api.get('/finance/gl/journals', { params }),
  getGLJournalEntryById: (id, params = {}) => api.get(`/finance/gl/journals/${id}`, { params }),
  createGLJournalEntry: (data, params = {}) => api.post('/finance/gl/journals', data, { params }),
  updateGLJournalEntry: (id, data, params = {}) => api.put(`/finance/gl/journals/${id}`, data, { params }),
  deleteGLJournalEntry: (id, params = {}) => api.delete(`/finance/gl/journals/${id}`, { params }),
  postGLJournalEntry: (id, params = {}) => api.post(`/finance/gl/journals/${id}/post`, {}, { params })
}

// Tax API
export const taxesAPI = {
  getRules: () => api.get('/taxes/rules'),
  getCompanyLocation: () => api.get('/taxes/company-location'),
  getPeriods: (params = {}) => api.get('/taxes/periods', { params }),
  createPeriod: (data) => api.post('/taxes/periods', data),
  updatePeriod: (id, data) => api.put(`/taxes/periods/${id}`, data),
  closePeriod: (id) => api.post(`/taxes/periods/${id}/close`),
  getLiabilities: (params = {}) => api.get('/taxes/liabilities', { params }),
  createLiability: (data) => api.post('/taxes/liabilities', data),
  updateLiability: (id, data) => api.put(`/taxes/liabilities/${id}`, data),
  deleteLiability: (id) => api.delete(`/taxes/liabilities/${id}`),
  getFilings: (params = {}) => api.get('/taxes/filings', { params }),
  createFiling: (data) => api.post('/taxes/filings', data),
  updateFiling: (id, data) => api.put(`/taxes/filings/${id}`, data),
  deleteFiling: (id) => api.delete(`/taxes/filings/${id}`)
};

// Workflow API
export const workflowAPI = {
  getAll: () => api.get('/workflows'),
  getStats: () => api.get('/workflows/stats'),
  create: (data) => api.post('/workflows', data),
  update: (id, data) => api.put(`/workflows/${id}`, data),
  delete: (id) => api.delete(`/workflows/${id}`),
  initiate: (data) => api.post('/workflows/initiate', data),
  action: (data) => api.post('/workflows/action', data),
  getPending: (userId) => api.get('/workflows/pending', { params: { userId } })
}

// Help API
export const helpAPI = {
  getStructure: () => api.get('/help/structure'),
  getArticle: (slug) => api.get(`/help/${slug}`),
  search: (params) => api.get('/help/search', { params }),
  submitFeedback: (data) => api.post('/help/feedback', data),
  getStats: () => api.get('/help/stats')
}

export const ticketsAPI = {
  create: (data) => api.post('/tickets', data),
  list: (params = {}) => api.get('/tickets', { params }),
  getById: (id) => api.get(`/tickets/${id}`),
  getSuggestions: (id) => api.get(`/tickets/${id}/suggestions`),
  timeline: (id) => api.get(`/tickets/${id}/timeline`),
  getDraftSuggestions: (data) => api.post('/tickets/suggestions', data),
  dashboard: (params = {}) => api.get('/tickets/dashboard', { params }),
  audit: (params = {}) => api.get('/tickets/audit', { params }),
  close: (id) => api.post(`/tickets/${id}/close`),
  updateAdmin: (id, data) => api.patch(`/tickets/${id}`, data),
  updateSelf: (id, data) => api.patch(`/tickets/${id}/self`, data),
  updateStatus: (id, data) => api.patch(`/tickets/${id}/status`, data),
  cancel: (id) => api.delete(`/tickets/${id}`)
}

// Integrity API
export const integrityAPI = {
  checkDependencies: (module, id) => api.get('/integrity/check', { params: { module, id } }),
  deleteRecord: (data) => api.post('/integrity/delete', data)
}

// Vendor Dashboard API
export const vendorDashboardAPI = {
  getStats: () => api.get('/vendor-dashboard/stats'),
  getRFxRecommendations: () => api.get('/vendor-dashboard/recommendations'),
  getBidInsights: (rfxId) => api.get(`/vendor-dashboard/bid-insights/${rfxId}`)
}

// GRC API
export const grcAPI = {
  getExceptions: () => api.get('/grc/exceptions')
}

export const auditLogsAPI = {
  list: (params = {}) => api.get('/audit-logs', { params })
}

export const moduleRegistryAPI = {
  getAll: (params = {}) => api.get('/module-registry', { params }),
  getGraph: () => api.get('/module-registry/graph'),
  resolve: (modules) => api.post('/module-registry/resolve', { modules })
}

export const featureFlagsAPI = {
  list: (params = {}) => api.get('/feature-flags', { params }),
  upsert: (data) => api.post('/feature-flags', data),
  setModuleEnabled: (module_code, is_enabled) =>
    api.post('/feature-flags', { module_code, feature_name: '__module_enabled__', is_enabled })
}

// Role Management API
export const rolesAPI = {
  getAll: () => api.get('/roles'),
  getPermissions: () => api.get('/roles/permissions'),
  initPermissions: () => api.post('/roles/init-permissions'),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`)
}

// User Management API
export const usersAPI = {
  getAll: (params = {}) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getManagers: () => api.get('/users/managers')
}

export const apiClient = api;
export default api
